import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { InterviewType } from 'common/models/Configuration/InterviewType';
import { JobTracking } from 'common/models/Jobs/JobTracking';
import InfoButtonToolTip from 'components/InfoButtonToolTip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetActiveInterviewTypes, SetInterviewSlotCandidate } from 'services/InterviewsService';
import { GetJobCandidates } from 'services/JobsService';

interface Props {
  open: boolean;
  interviewId: number;
  jobId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string) => void;
  errorHandler?: (message: string) => void;
}

export default function AddCandidateToInterviewSlotDialog({
  open,
  interviewId,
  jobId,
  closeHandler,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [interviewTypes, setInterviewTypes] = useState<InterviewType[]>([]);
  const [linkedCandidates, setLinkedCandidates] = useState<JobTracking[]>([]);
  const [candidateId, setCandidateId] = useState(0);
  const [typeId, setTypeId] = useState(0);

  useEffect(() => {
    const getData = async () => {
      loadingHandler && loadingHandler(true);
      const res = await GetActiveInterviewTypes();
      if (res) {
        const types = res.filter((t) => !t.isSlot);
        setInterviewTypes(types);
      }

      loadingHandler && loadingHandler(false);
    };
    getData();
  }, [loadingHandler]);

  useEffect(() => {
    const getLinkedCandidates = async () => {
      const res = await GetJobCandidates(jobId);
      if (res) setLinkedCandidates(res);
    };
    getLinkedCandidates();
  }, [jobId]);

  useEffect(() => {
    if (!open) {
      setCandidateId(0);
      setTypeId(0);
    }
  }, [open]);

  const selectedCandidate = useMemo(() => {
    if (candidateId !== 0) {
      const cand = linkedCandidates.find((c) => c.id === candidateId);
      if (cand) return cand;
    }
    return null;
  }, [candidateId, linkedCandidates]);

  const submitHandler = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const res = await SetInterviewSlotCandidate(interviewId, typeId, candidateId, errorHandler);
    loadingHandler && loadingHandler(false);

    if (res) successHandler && successHandler('Candidate Added');
  }, [candidateId, errorHandler, interviewId, loadingHandler, successHandler, typeId]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Add Candidate</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt="10px">
          <TextField
            select
            label="Interview Type"
            value={typeId.toString()}
            onChange={({ target }) => setTypeId(+target.value)}
          >
            <MenuItem value="0">None</MenuItem>
            {interviewTypes.map((t) => (
              <MenuItem key={t.id} value={t.id.toString()}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <Box display="flex">
            <Autocomplete
              size="small"
              value={selectedCandidate}
              options={linkedCandidates}
              getOptionLabel={(o) => o.fullName}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => setCandidateId(v ? v.id : 0)}
              sx={{ flex: '1 1 0' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Linked Candidate"
                  inputProps={{ ...params.inputProps, 'data-lpignore': 'true' }}
                />
              )}
            />
            <InfoButtonToolTip text="Only candidates already linked to the interview's job will be available" />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={candidateId === 0 || typeId === 0}
          onClick={submitHandler}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
